import { AddCircle } from '@mui/icons-material';
import { Box, Button, Chip, Icon, Link, SxProps, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  ICompanyType,
  IMarketActivity,
  IMembershipType,
  IProductCode,
  ITitleInternal,
  useCompanyTypesQuery,
  useDeleteValueMutation,
  useMarketActivitiesQuery,
  useMembershipTypesQuery,
  useProductCodesQuery,
  useTitlesInternalQuery
} from '../../generated/graphql';
import { removeValueFromCache } from '../../utils/clientUtils';
import { ValueListNames } from '../../utils/constants';
import { Colors } from '../../utils/theme';
import OverviewToolbar from '../Common/Layout/OverviewToolbar';
import OverviewTable from '../Common/Tables/OverviewTable';
import ValueList from './ValueList';

type TableItem = {
  name: string;
  values: ValueType[];
};

export type ValueType =
  | ICompanyType
  | IMarketActivity
  | IMembershipType
  | IProductCode
  | ITitleInternal;

const chipStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  fontWeight: 'normal',
  background: '#69AAF333',
  '&:active, &:focus': {
    background: '#69AAF333'
  }
};

const deleteIconStyles: SxProps = {
  color: Colors.skyBlue,
  width: 28,
  height: 28,
  '&:hover': {
    color: Colors.main
  }
};

const Customization: React.FunctionComponent = () => {
  const { dispatch } = useContext(AppContext);
  const [tableData, setTableData] = useState<TableItem[]>([]);
  const [selectedValueList, setSelectedValueList] = useState('');
  const [values, setValues] = useState<ValueType[]>([]);
  const [openValueList, setOpenValueList] = useState(false);
  const [addNewValue, setAddNewValue] = useState(false);
  const companyTypes = useCompanyTypesQuery();
  const marketActivities = useMarketActivitiesQuery();
  const membershipTypes = useMembershipTypesQuery();
  const productCodes = useProductCodesQuery();
  const titlesInternal = useTitlesInternalQuery();
  const [deleteValueMutation] = useDeleteValueMutation();

  useEffect(() => {
    let res = [...tableData];
    if (marketActivities.data && marketActivities.data.result.length) {
      if (
        res.filter((i) => i.name === marketActivities?.data?.result[0]?.__typename).length === 0
      ) {
        res = [
          ...res,
          {
            name: marketActivities.data.result[0]?.__typename,
            values: marketActivities.data.result
          }
        ] as TableItem[];
      } else {
        res = res.map((r) =>
          r.name === marketActivities?.data?.result[0]?.__typename
            ? { ...r, values: marketActivities.data.result }
            : r
        ) as TableItem[];
      }
    }
    if (membershipTypes.data && membershipTypes.data.result.length) {
      if (res.filter((i) => i.name === membershipTypes?.data?.result[0]?.__typename).length === 0) {
        res = [
          ...res,
          { name: membershipTypes.data.result[0]?.__typename, values: membershipTypes.data.result }
        ] as TableItem[];
      } else {
        res = res.map((r) =>
          r.name === membershipTypes?.data?.result[0]?.__typename
            ? { ...r, values: membershipTypes.data.result }
            : r
        ) as TableItem[];
      }
    }
    if (companyTypes.data && companyTypes.data.result.length) {
      if (res.filter((i) => i.name === companyTypes?.data?.result[0]?.__typename).length === 0) {
        res = [
          ...res,
          { name: companyTypes.data.result[0]?.__typename, values: companyTypes.data.result }
        ] as TableItem[];
      } else {
        res = res.map((r) =>
          r.name === companyTypes?.data?.result[0]?.__typename
            ? { ...r, values: companyTypes.data.result }
            : r
        ) as TableItem[];
      }
    }
    if (productCodes.data && productCodes.data.result.length) {
      if (res.filter((i) => i.name === productCodes?.data?.result[0]?.__typename).length === 0) {
        res = [
          ...res,
          { name: productCodes.data.result[0]?.__typename, values: productCodes.data.result }
        ] as TableItem[];
      } else {
        res = res.map((r) =>
          r.name === productCodes?.data?.result[0]?.__typename
            ? { ...r, values: productCodes.data.result }
            : r
        ) as TableItem[];
      }
    }
    if (titlesInternal.data && titlesInternal.data.result.length) {
      if (res.filter((i) => i.name === titlesInternal?.data?.result[0]?.__typename).length === 0) {
        res = [
          ...res,
          { name: titlesInternal.data.result[0]?.__typename, values: titlesInternal.data.result }
        ] as TableItem[];
      } else {
        res = res.map((r) =>
          r.name === titlesInternal?.data?.result[0]?.__typename
            ? { ...r, values: titlesInternal.data.result }
            : r
        ) as TableItem[];
      }
    }

    setTableData(
      res.sort(function (a, b) {
        const x = a.name.toLowerCase();
        const y = b.name.toLowerCase();
        if (x < y) {
          return -1;
        }
        if (x > y) {
          return 1;
        }
        return 0;
      })
    );
  }, [
    companyTypes.data,
    marketActivities.data,
    membershipTypes.data,
    productCodes.data,
    titlesInternal.data
  ]);

  const renderValueListName = (rowData: TableItem) => {
    return (
      <Box sx={{ fontWeight: 'normal', fontSize: 14 }}>
        {rowData.name?.replace(/([A-Z][a-z])/g, ' $1').replace(/(\d)/g, ' $1')}
      </Box>
    );
  };

  const renderValues = (rowData: TableItem) => {
    const { name, values } = rowData;
    const count = values.length;
    const result = values.map((v: ValueType) => v.name).slice(0, 10);

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))' }}>
        <Box
          sx={{
            gridColumn: 'span 11',
            display: 'flex',
            gap: '10px',
            margin: '15px'
          }}
        >
          <Box sx={{ gridColumn: 'span 11', display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {result.map((val, index) => (
              <Box key={index}>
                <Chip
                  id={`${name}-${val}-val`}
                  data-testid={`${name}-${val}-val`}
                  label={val}
                  onDelete={() =>
                    dispatch({
                      type: Actions.ShowConfirmation,
                      payload: {
                        text: `Are you sure you want to delete ${val} from ${name
                          .replace(/([A-Z][a-z])/g, ' $1')
                          .replace(/(\d)/g, ' $1')} value list?`,
                        agreeAction: handleDeleteValueItem,
                        params: { val, rowData }
                      }
                    })
                  }
                  sx={{
                    ...chipStyles,
                    '.MuiChip-deleteIcon': deleteIconStyles
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ gridColumn: 'span 1' }}>
          {count > 10 && (
            <Box key={count} style={{ margin: 10 }}>
              <Link
                underline="always"
                onClick={() => handleOpenValueList(rowData.name, rowData.values)}
              >
                View all...
              </Link>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const handleDeleteValueItem = ({ val, rowData }: { val: string; rowData: TableItem }) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    const valueItem = rowData.values.find((v) => v.name === val);
    if (valueItem) {
      deleteValueMutation({
        variables: {
          value_list_name: ValueListNames[valueItem?.__typename as string],
          value_id: parseInt(valueItem?.id)
        },
        update() {
          removeValueFromCache(valueItem.__typename as string, valueItem);
          dispatch({
            type: Actions.ShowMessage,
            payload: { text: `Value ${val} deleted`, severity: 'success' }
          });
        }
      }).catch((e) => {
        const message = e.message.includes('update or delete on table')
          ? `Value ${val} cannot be deleted!`
          : e.message;
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: message,
            autoHide: 6000
          }
        });
      });
    }
  };

  const renderAddValue = (rowData: TableItem) => {
    return (
      <Button
        id={`${rowData.name}-add-value-button`}
        data-testid={`${rowData.name}-add-value-button`}
        onClick={() => handleOpenValueList(rowData.name, rowData.values, true)}
      >
        <AddCircle style={{ color: Colors.skyBlue, height: 42, width: 42 }} />
      </Button>
    );
  };

  const handleOpenValueList = (name: string, values: ValueType[], addNew = false) => {
    setSelectedValueList(name);
    setValues(values);
    setAddNewValue(addNew);
    setOpenValueList(true);
  };

  const handleCloseValueList = () => {
    setSelectedValueList('');
    setValues([]);
    setAddNewValue(false);
    setOpenValueList(false);
  };

  const columns = [
    { title: 'Value List Name', width: 350, sorting: false, render: renderValueListName },
    { title: 'Values', width: 1232, sorting: false, render: renderValues },
    { title: 'Add Value', width: 140, sorting: false, render: renderAddValue }
  ];

  return (
    <Box className="overviewPage">
      <Box sx={{ width: '100%', zIndex: 2 }}>
        <OverviewToolbar>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap'
            }}
          >
            <Icon sx={{ marginRight: '7px' }}>{'construction'}</Icon>
            <Typography>Customization</Typography>
          </Box>
        </OverviewToolbar>
      </Box>
      <Box>
        <OverviewTable
          columns={columns}
          data={tableData}
          loading={
            companyTypes.loading ||
            marketActivities.loading ||
            membershipTypes.loading ||
            productCodes.loading ||
            titlesInternal.loading
          }
          handleDetails={() => true}
          handleRemove={() => true}
          pageSize={10}
          hideSearch={true}
        />
      </Box>
      {openValueList && (
        <ValueList
          name={selectedValueList}
          values={values}
          addNew={addNewValue}
          handleClose={handleCloseValueList}
        />
      )}
    </Box>
  );
};

export default Customization;
